/**
 * Post-login landing route for the Auth0 redirect (`/callback`).
 *
 * What: Shows a spinner while the code exchange and role fetch settle, then
 * sends the user to their role-default page; surfaces a retryable error view
 * if the exchange fails (stale state, double-submit, blocked storage).
 * Why: Logins that carry an explicit destination navigate straight there via
 * `appState.returnTo` (see `Auth0ProviderWithNavigate`); this route only
 * handles logins without one, which need the role loaded before a
 * destination exists. Navigating blindly to `/` dumped signed-in users on
 * the marketing landing page.
 * When: Update when changing post-login routing, role defaults, or the
 * `/callback` error handling.
 *
 * Every role logs in through here, students and parents included, so the
 * copy names no role, and the way out is the public home page. Auth0's own
 * error text ("Invalid state") never reaches the screen; it goes to the
 * console, where a failed log-in can still be traced.
 */
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, Stack, Typography } from '@mui/material';

import { accountStateOf } from '@/auth/accountState';
import {
  buildGuidedSignupLoginOptions,
  isGuidedSignupCallbackError,
} from '@/auth/guidedSignupAuth';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import AccountLoadError from '@/components/routing/AccountLoadError';
import { PUBLIC_HOME } from '@/config/navigation';
import { useAuth } from '@/hooks/useAuth';
import { getDefaultRedirectPath } from '@/utils/accessControl';
import { landingStateFor } from '@/utils/teacherEventSource';

function AuthCallback() {
  const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0();
  const { user, userRole, isRoleLoaded, userStatus, authError, refetchRole } =
    useAuth();
  const navigate = useNavigate();
  const retryGuidedSignup = isGuidedSignupCallbackError(error);
  // A failed account check has no role to route by; the student default the
  // provider starts with would send a teacher to the student home.
  const accountCheckFailed =
    accountStateOf({
      isRoleLoaded,
      userStatus,
      authError: authError ?? null,
    }) === 'failed';

  const handleRetry = () => {
    if (retryGuidedSignup) {
      void loginWithRedirect(buildGuidedSignupLoginOptions());
      return;
    }
    void loginWithRedirect();
  };

  useEffect(() => {
    if (error) console.error('Auth0 callback error:', error);
  }, [error]);

  useEffect(() => {
    if (isLoading || error) return;

    // Pre-appState bundles stored the destination here; clear any leftover
    // key so it can't shadow the current mechanism.
    localStorage.removeItem('returnTo');

    if (!isAuthenticated) {
      // Direct visit without a session (bookmarked /callback, cancelled
      // login) — nothing to resume, back to the homepage.
      navigate('/', { replace: true });
      return;
    }

    if (isRoleLoaded && !accountCheckFailed) {
      const destination = getDefaultRedirectPath(userRole, user);
      // The Students class page records this open as a landing, not a choice.
      navigate(destination, {
        replace: true,
        state: landingStateFor(destination),
      });
    }
  }, [
    isLoading,
    error,
    isAuthenticated,
    isRoleLoaded,
    accountCheckFailed,
    userRole,
    user,
    navigate,
  ]);

  if (error) {
    return (
      <>
        <Helmet>
          <title>Couldn’t log you in | DeepThink</title>
        </Helmet>
        <Box
          sx={{
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
          }}
        >
          <Stack spacing={2} alignItems="center" sx={{ maxWidth: 420 }}>
            <Typography variant="h5" component="h1" fontWeight={700}>
              Couldn’t log you in
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              The log-in was interrupted or took too long. Trying again usually
              fixes this.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button variant="contained" color="primary" onClick={handleRetry}>
                Try again
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate(PUBLIC_HOME.path, { replace: true })}
              >
                Go to {PUBLIC_HOME.label}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </>
    );
  }

  if (accountCheckFailed) {
    return (
      <>
        <Helmet>
          <title>Couldn’t load your account | DeepThink</title>
        </Helmet>
        <AccountLoadError onRetry={refetchRole} />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Logging in | DeepThink</title>
      </Helmet>
      <LoadingSpinner />
    </>
  );
}

export default AuthCallback;
